// src/services/publicUtilitiesApi.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Public Utilities v2 API client
// --- UPDATE (Public Utilities v2) — NEW FILE ------------------------------------------------
// Backend: routes/publicUtilitiesRoutes.js mounted at /api/v1/public-utilities.
// Photos use the tech-app endpoint /asset-field/:assetId/photos (same table, same files).
// Every call returns the `data` payload or throws an Error with the server's message.
// --- END UPDATE ------------------------------------------------------------------------------
import { authenticatedFetch } from './api';
import { getAuthToken } from '../utils/auth';
import { config } from '../config';

const BASE = '/public-utilities';
const json = (body) => ({ body: JSON.stringify(body ?? {}), headers: { 'Content-Type': 'application/json' } });
const unwrap = (res) => {
    if (res && typeof res === 'object' && 'success' in res) {
        if (res.success === false) throw new Error(res.message || 'Request failed');
        return res.data;
    }
    return res;
};
const qs = (params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, String(v)); });
    const s = p.toString();
    return s ? `?${s}` : '';
};

// catalogue + intelligence
export const getCatalog = async () => unwrap(await authenticatedFetch(`${BASE}/catalog`));
export const getOverview = async (refresh = false) => unwrap(await authenticatedFetch(`${BASE}/overview${refresh ? '?refresh=1' : ''}`));
export const getBrief = async () => unwrap(await authenticatedFetch(`${BASE}/ai/brief`));

/** Ask FlowSense (Ollama) with the Public Utilities context. */
export const askPublicUtilitiesAi = async (question, context) => {
    const q = String(question || '').trim().slice(0, 1000);
    if (!q) throw new Error('Ask a question first.');
    const res = await authenticatedFetch('/flowsense-ai/ask', { method: 'POST', ...json({ question: q, context: context || {}, module: 'public_utilities' }) });
    return res?.data || res;
};

// --- UPDATE (Public Utilities v3): PU-only GIS layer (compact wire format → feature objects)
/** Expand { fields, rows } into feature objects; tolerates the plain `features` shape too. */
export const expandMapLayer = (data) => {
    if (!data || typeof data !== 'object') return { features: [], zones: [], managers: [], legend: { types: [], status: {}, due: {} }, total: 0, no_gps: 0 };
    if (Array.isArray(data.features)) return data;
    const fields = Array.isArray(data.fields) ? data.fields : [];
    const features = (data.rows || []).map((r) => { const o = {}; for (let i = 0; i < fields.length; i++) o[fields[i]] = r[i]; return o; });
    const { rows, fields: _f, ...rest } = data; // eslint-disable-line no-unused-vars
    return { ...rest, features };
};
/** @param {{types?:string[]|string,status?:string[]|string,manager_id?,zone_id?,q?,bbox?}} params */
export const getMapLayer = async (params = {}) => {
    const p = { ...params };
    if (Array.isArray(p.types)) p.types = p.types.join(',');
    if (Array.isArray(p.status)) p.status = p.status.join(',');
    return expandMapLayer(unwrap(await authenticatedFetch(`${BASE}/map${qs(p)}`)));
};
// --- END UPDATE

// zones / constituencies
export const getZones = async (refresh = false) => unwrap(await authenticatedFetch(`${BASE}/zones${refresh ? '?refresh=1' : ''}`));
export const saveConstituencyCode = async (code, body) => unwrap(await authenticatedFetch(`${BASE}/zones/codes/${encodeURIComponent(code)}`, { method: 'PUT', ...json(body) }));
// --- UPDATE (v3): which constituency a point is in (GPS → polygon → code → name)
export const locateZone = async (lat, lng) => unwrap(await authenticatedFetch(`${BASE}/zones/locate${qs({ lat, lng })}`));
/** Public Utilities assets within ~radiusM of a point (duplicate guard for the asset form). */
export const findNearbyAssets = async (lat, lng, radiusM = 15) => {
    const la = Number(lat); const lo = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return [];
    const dLat = radiusM / 111320; const dLng = radiusM / (111320 * Math.cos((la * Math.PI) / 180));
    const layer = await getMapLayer({ bbox: [lo - dLng, la - dLat, lo + dLng, la + dLat].map((x) => x.toFixed(6)).join(',') });
    return layer.features || [];
};
// --- END UPDATE
export const rezoneAssets = async (all = false) => unwrap(await authenticatedFetch(`${BASE}/zones/rezone`, { method: 'POST', ...json({ all }) }));

// managers
export const listManagers = async () => unwrap(await authenticatedFetch(`${BASE}/managers`));
export const createManager = async (body) => unwrap(await authenticatedFetch(`${BASE}/managers`, { method: 'POST', ...json(body) }));
export const updateManager = async (id, body) => unwrap(await authenticatedFetch(`${BASE}/managers/${encodeURIComponent(id)}`, { method: 'PUT', ...json(body) }));
export const deleteManager = async (id) => unwrap(await authenticatedFetch(`${BASE}/managers/${encodeURIComponent(id)}`, { method: 'DELETE' }));
export const assignManager = async (assetIds, managerId) => unwrap(await authenticatedFetch(`${BASE}/managers/assign`, { method: 'POST', ...json({ assetIds, managerId }) }));
// --- UPDATE (Public Utilities v4 — field access): people linked to a manager
export const listManagerMembers = async (managerId) => unwrap(await authenticatedFetch(`${BASE}/managers/${encodeURIComponent(managerId)}/members`));
export const addManagerMembers = async (managerId, userIds, memberRole = 'member') => unwrap(await authenticatedFetch(`${BASE}/managers/${encodeURIComponent(managerId)}/members`, { method: 'POST', ...json({ user_ids: userIds, member_role: memberRole }) }));
export const removeManagerMember = async (managerId, userId) => unwrap(await authenticatedFetch(`${BASE}/managers/${encodeURIComponent(managerId)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE' }));
export const searchFieldUsers = async (q) => unwrap(await authenticatedFetch(`${BASE}/field-users${qs({ q })}`));
export const getMyAccess = async () => unwrap(await authenticatedFetch(`${BASE}/me/access`));
// --- END UPDATE

// import
export const previewImport = async (file, sheets) => {
    const fd = new FormData();
    fd.append('file', file);
    if (Array.isArray(sheets) && sheets.length) fd.append('sheets', JSON.stringify(sheets));
    return unwrap(await authenticatedFetch(`${BASE}/import/preview`, { method: 'POST', body: fd }));
};
export const commitImport = async (batchId, options) => unwrap(await authenticatedFetch(`${BASE}/import/${encodeURIComponent(batchId)}/commit`, { method: 'POST', ...json(options) }));
export const rollbackImport = async (batchId) => unwrap(await authenticatedFetch(`${BASE}/import/${encodeURIComponent(batchId)}/rollback`, { method: 'POST', ...json({}) }));
export const listImports = async () => unwrap(await authenticatedFetch(`${BASE}/import`));
export const getImport = async (batchId) => unwrap(await authenticatedFetch(`${BASE}/import/${encodeURIComponent(batchId)}`));
/** Downloads the rows that need attention as CSV (fetch + blob so the auth header is sent). */
export const downloadImportIssues = async (batchId) => {
    const res = await fetch(`${config.apiUrl}${BASE}/import/${encodeURIComponent(batchId)}/issues.csv`, { headers: { Authorization: `Bearer ${getAuthToken() || ''}` } });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `import-issues-${String(batchId).slice(0, 8)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
};

// asset lifecycle
export const getAssetProfile = async (id) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/profile`));
export const getAssetTimeline = async (id, limit = 200) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/timeline${qs({ limit })}`));
export const getAssetPhotos = async (id) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/photos`));
export const logMaintenance = async (id, body) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/maintenance`, { method: 'POST', ...json(body) }));
export const listSchedules = async (id) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/schedules`));
export const createSchedule = async (id, body) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/schedules`, { method: 'POST', ...json(body) }));
export const updateSchedule = async (scheduleId, body) => unwrap(await authenticatedFetch(`${BASE}/schedules/${encodeURIComponent(scheduleId)}`, { method: 'PUT', ...json(body) }));
export const deleteSchedule = async (scheduleId) => unwrap(await authenticatedFetch(`${BASE}/schedules/${encodeURIComponent(scheduleId)}`, { method: 'DELETE' }));
export const bulkSchedule = async (body) => unwrap(await authenticatedFetch(`${BASE}/schedules/bulk`, { method: 'POST', ...json(body) }));
export const getDueInspections = async (params) => unwrap(await authenticatedFetch(`${BASE}/inspections/due${qs(params)}`));
export const listInspections = async (id) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/inspections`));
export const submitInspection = async (id, body) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/inspections`, { method: 'POST', ...json(body) }));
export const setAssetStatus = async (id, status, reason) => unwrap(await authenticatedFetch(`${BASE}/assets/${encodeURIComponent(id)}/status`, { method: 'PATCH', ...json({ status, reason }) }));

/** Upload photos through the tech-app endpoint (multipart "photos"). Returns the stored photo rows. */
export const uploadAssetPhotos = async (assetId, files, { kind = 'inspection', caption = '' } = {}) => {
    const fd = new FormData();
    Array.from(files || []).slice(0, 10).forEach((f) => fd.append('photos', f));
    fd.append('kind', kind);
    if (caption) fd.append('caption', caption);
    const res = await authenticatedFetch(`/asset-field/${encodeURIComponent(assetId)}/photos`, { method: 'POST', body: fd });
    const data = res?.data ?? res;
    return Array.isArray(data) ? data : (data?.photos || []);
};

const publicUtilitiesApi = {
    getCatalog, getOverview, getBrief, askPublicUtilitiesAi, getZones, saveConstituencyCode, rezoneAssets,
    listManagers, createManager, updateManager, deleteManager, assignManager,
    listManagerMembers, addManagerMembers, removeManagerMember, searchFieldUsers, getMyAccess, // --- UPDATE (v4)
    previewImport, commitImport, rollbackImport, listImports, getImport, downloadImportIssues,
    getAssetProfile, getAssetTimeline, getAssetPhotos, logMaintenance, listSchedules, createSchedule, updateSchedule, deleteSchedule,
    bulkSchedule, getDueInspections, listInspections, submitInspection, setAssetStatus, uploadAssetPhotos,
};
export default publicUtilitiesApi;
